import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  Avatar,
  Divider,
  Stack,
  TextField,
  Paper,
  Tooltip,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StoreIcon from '@mui/icons-material/Store';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SendIcon from '@mui/icons-material/Send';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import { useSelector } from 'react-redux';

// ---------------------------------------------------------------------------
// Enums — mirrored from the TypeORM entities.
// ---------------------------------------------------------------------------

export const TacheStatus = {
  EN_ATTENTE: 'en-attente',
  EN_COURS: 'en-cours',
  TRAITE: 'traite',
  ANNULE: 'annule',
};

export const TacheType = {
  cmd_a_dessiner: 'cmd-a-dessiner',
  cmd_a_valider: 'cmd-a-valider',
  cmd_a_valider_cq: 'cmd-a-valider-cq',
  cmd_a_saisir: 'cmd-a-saisir',
  cmd_a_ettiqueter: 'cmd-a-ettiqueter',
  cmd_a_envoyer: 'cmd-a-envoyer',
  cmd_a_receptionner: 'cmd-a-receptionner',
  cmd_a_livrer: 'cmd-a-livrer',
  cmd_a_resoudre: 'cmd-a-resoudre',
  cmd_a_expedier: 'cmd-a-expedier',
  cmd_a_debloquer: 'cmd-a-debloquer',
  autre: 'autre',
};

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

const STATUS_CONFIG = {
  [TacheStatus.EN_ATTENTE]: { label: 'En attente', color: '#8a6d1d', bg: '#fdf1d6' },
  [TacheStatus.EN_COURS]: { label: 'En cours', color: '#1d5b8a', bg: '#dcedfb' },
  [TacheStatus.TRAITE]: { label: 'Traité', color: '#1e7d46', bg: '#dcf5e6' },
  [TacheStatus.ANNULE]: { label: 'Annulé', color: '#8a1d1d', bg: '#fbdcdc' },
};

const TYPE_LABELS = {
  [TacheType.cmd_a_dessiner]: 'Commande à dessiner',
  [TacheType.cmd_a_valider]: 'Commande à valider',
  [TacheType.cmd_a_valider_cq]: 'Commande à valider (CQ)',
  [TacheType.cmd_a_saisir]: 'Commande à saisir',
  [TacheType.cmd_a_ettiqueter]: 'Commande à étiqueter',
  [TacheType.cmd_a_envoyer]: 'Commande à envoyer',
  [TacheType.cmd_a_receptionner]: 'Commande à réceptionner',
  [TacheType.cmd_a_livrer]: 'Commande à livrer',
  [TacheType.cmd_a_resoudre]: 'Commande à résoudre',
  [TacheType.cmd_a_expedier]: 'Commande à expédier',
  [TacheType.cmd_a_debloquer]: 'Commande à débloquer',
  [TacheType.autre]: 'Autre',
};

const TYPE_COLORS = {
  [TacheType.cmd_a_dessiner]: '#966baf',
  [TacheType.cmd_a_valider]: '#FFB84D',
  [TacheType.cmd_a_valider_cq]: '#FF8C00',
  [TacheType.cmd_a_saisir]: '#4CAF50',
  [TacheType.cmd_a_ettiqueter]: '#00BCD4',
  [TacheType.cmd_a_envoyer]: '#2196F3',
  [TacheType.cmd_a_receptionner]: '#977ac8',
  [TacheType.cmd_a_livrer]: '#578d59',
  [TacheType.cmd_a_resoudre]: '#ba4941',
  [TacheType.cmd_a_expedier]: '#508552',
  [TacheType.cmd_a_debloquer]: '#ca4f2d',
  [TacheType.autre]: '#9e84a3',
};

function initials(user) {
  const first = user.prenom?.[0] ?? user.userName?.[0] ?? '?';
  const last = user.nom?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
}

function displayName(user) {
  if (user.prenom || user.nom) return `${user.prenom ?? ''} ${user.nom ?? ''}`.trim();
  return user.userName ?? `Utilisateur #${user.idUser}`;
}

function formatDate(value) {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// The current user posting a comment — wire this to your auth context.
const CURRENT_USER_NAME = 'Utilisateur actuel';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function TacheDetails({ show, onClose, onUpdate, tache, onEdit, onDelete }) {
  const [commentText, setCommentText] = useState('');
  const { loggedUser } = useSelector((state) => state.auth_store);

  if (!tache) return null;

  const statusInfo = STATUS_CONFIG[tache.status] ?? {
    label: tache.status,
    color: '#555',
    bg: '#eee',
  };
  const typeLabel = TYPE_LABELS[tache.type] ?? tache.type;
  const comments = tache.commentaires ?? [];

  const handleAddComment = () => {
    const trimmed = commentText.trim();
    if (!trimmed) return;

    const newComment = {
      content: trimmed,
      userName: `${loggedUser?.nom || '?'} ${loggedUser?.prenom || '?'}`,
      date: new Date().toISOString(),
    };

    const updatedTache = {
      ...tache,
      commentaires: [...comments, newComment],
    };

    onUpdate(updatedTache);
    setCommentText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleAddComment();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={show}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 460 }, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body1" sx={{ color: 'text.secondary', letterSpacing: 0.5 }}>
              Tâche #000{tache.idTache}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mt: -0.5,
                borderBottom: `${TYPE_COLORS[tache?.type] ?? 'gray'} 4px solid`,
              }}
            >
              {typeLabel}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Fermer">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={statusInfo.label}
            sx={{
              bgcolor: statusInfo.bg,
              color: statusInfo.color,
              fontWeight: 600,
              border: 'none',
            }}
          />
          {tache.is_urgent && (
            <Chip
              size="small"
              icon={<WarningAmberIcon sx={{ fontSize: 16, color: '#a13d1f !important' }} />}
              label={tache.date_urgent ? `Urgent · ${formatDate(tache.date_urgent)}` : 'Urgent'}
              sx={{ bgcolor: '#fde3d6', color: '#a13d1f', fontWeight: 600 }}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          {tache.status === 'en-attente' && (
            <Button
              size="small"
              onClick={() => onUpdate({ idTache: tache?.idTache, status: 'en-cours'})}
              variant="outlined"
              color="primary"
            >
              Mettre en cours
            </Button>
          )}
          {tache.status === 'en-cours' && (
            <Button
              size="small"
              onClick={() => onUpdate({ idTache: tache?.idTache, status: 'traite', doneBy : `${loggedUser?.nom || '?'} ${loggedUser?.prenom || '?'}` })}
              variant="outlined"
              color="success"
              sx={{ ml: 1 }}
            >
              Mettre traité
            </Button>
          )}
          <Button
            size="small"
            onClick={() => onEdit(tache)}
            variant="outlined"
            color="secondary"
            sx={{ ml: 1 }}
          >
            Modifier
          </Button>
          <Button
            size="small"
            onClick={() => onDelete(tache?.idTache)}
            variant="outlined"
            color="error"
            sx={{ ml: 1 }}
          >
            Annuler
          </Button>
        </Stack>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        {/* Description */}
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
          Description
        </Typography>
        <Typography variant="body2" sx={{ mb: 2.5, whiteSpace: 'pre-wrap' }}>
          {tache.description || 'Aucune description fournie.'}
        </Typography>

        {/* Commande / Magasin */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.25,
              flex: 1,
              borderRadius: 2,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <LocalShippingIcon fontSize="small" sx={{ color: 'primary.main' }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Commande
              </Typography>
              <Typography variant="body2" fontWeight={600} noWrap>
                {tache?.commande?.ref ?? `Aucune`}
              </Typography>
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 1.25,
              flex: 1,
              borderRadius: 2,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <StoreIcon fontSize="small" sx={{ color: 'primary.main' }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Magasin
              </Typography>
              <Typography variant="body2" fontWeight={600} noWrap>
                {tache?.magasin?.code ?? `Aucune`}
              </Typography>
            </Box>
          </Paper>
        </Stack>

        {/* Assigned users */}
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
          Assignée à
        </Typography>
        {tache.users?.length ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
            {tache.users.map((u) => (
              <Chip
                key={u.idUser}
                avatar={
                  u.avatarUrl ? (
                    <Avatar src={u.avatarUrl} />
                  ) : (
                    <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark' }}>
                      {initials(u)}
                    </Avatar>
                  )
                }
                label={displayName(u)}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Aucun utilisateur assigné.
          </Typography>
        )}

        {/* Meta info */}
        <Stack spacing={1} sx={{ mb: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              Crée par <strong style={{ color: '#50a9ed' }}>{tache?.createdBy || 'CRM'}</strong> ·{' '}
              {formatDate(tache?.created_at)}
            </Typography>
          </Stack>
          {tache?.doneBy && (
            <Stack direction="row" spacing={1} alignItems="center">
              <EventIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                Traitée par <strong style={{ color: '#2ac077' }}>{tache?.doneBy}</strong> ·{' '}
                {formatDate(tache?.updated_at)}
              </Typography>
            </Stack>
          )}
        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        {/* Comments */}
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          Commentaires {comments.length > 0 && `(${comments.length})`}
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {comments.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Aucun commentaire pour le moment.
            </Typography>
          )}
          {comments.map((c, idx) => (
            <Paper key={idx} variant="elevation" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: 'primary.main' }}>
                    {c.userName?.[0]?.toUpperCase() ?? '?'}
                  </Avatar>
                  <Typography variant="body2" fontWeight={600}>
                    {c.userName}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(c.date)}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {c.content}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </Box>

      {/* Add comment — pinned footer */}
      <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Ajouter un commentaire… (Ctrl+Entrée pour envoyer)"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Tooltip title="Envoyer">
            <span>
              <IconButton
                color="primary"
                onClick={handleAddComment}
                disabled={!commentText.trim()}
                sx={{
                  bgcolor: commentText.trim() ? 'primary.main' : 'action.disabledBackground',
                  color: commentText.trim() ? 'primary.contrastText' : 'action.disabled',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Drawer>
  );
}
